<template>
  <div>
    <div ref="map" style="width: 100%; height: 400px"></div>
    <v-btn v-if="markers.length > 1" depressed color="success" @click="fitBounds" class="ma-1 float-right my-2">
      Fit
      <v-icon right dark>
        mdi-fit-to-screen-outline
      </v-icon>
    </v-btn>
  </div>
</template>
<script>
import { reverseGeocode } from "@/services/MapboxGeocoding";

// Same props and events as GoogleMapLoader.vue, rendered with Mapbox GL JS.
// Mapbox GL is loaded from the Mapbox CDN at runtime (like the Google Maps
// script), which keeps it out of the webpack build.
const MAPBOX_GL_VERSION = "3.9.4";
const MAPBOX_GL_URL = "https://api.mapbox.com/mapbox-gl-js/v" + MAPBOX_GL_VERSION + "/mapbox-gl";
const POLYLINES_SOURCE = "polylines";

let mapboxGlPromise = null;

function loadMapboxGl() {
  if (mapboxGlPromise == null) {
    mapboxGlPromise = new Promise((resolve, reject) => {
      if (window.mapboxgl) {
        resolve(window.mapboxgl);
        return;
      }
      const css = document.createElement("link");
      css.rel = "stylesheet";
      css.href = MAPBOX_GL_URL + ".css";
      document.head.appendChild(css);

      const script = document.createElement("script");
      script.src = MAPBOX_GL_URL + ".js";
      script.async = true;
      script.onload = () => resolve(window.mapboxgl);
      script.onerror = () => {
        mapboxGlPromise = null;
        reject(new Error("Could not load Mapbox GL JS"));
      };
      document.head.appendChild(script);
    });
  }
  return mapboxGlPromise;
}

function toLngLat(position) {
  return [parseFloat(position.lng), parseFloat(position.lat)];
}

function isValidPosition(position) {
  return position != null && !isNaN(parseFloat(position.lat)) && !isNaN(parseFloat(position.lng));
}

export default {
  props: {
    center: Object,
    zoom: Number,
    config: Object,
    markers: Array,
    selected: String,
    enabled: Boolean,
    polylines: []
  },
  data() {
    return {
      currentMidx: null,
    };
  },
  watch: {
    markers: function (newVal, oldVal) {
      this.fitBounds();
    },
    // marker positions and info texts are updated in place (e.g. live tracking)
    markersState: {
      handler: function () {
        this.renderMarkers();
      },
      deep: true,
    },
    polylines: {
      handler: function () {
        this.renderPolylines();
      },
      deep: true,
    },
    center: function (newVal) {
      if (this.map && isValidPosition(newVal)) {
        this.map.setCenter(toLngLat(newVal));
      }
    },
    zoom: function (newVal) {
      if (this.map) {
        this.map.setZoom(this.mapboxZoom(newVal));
      }
    },
    selected: function (newVal, oldVal) {
      let selectedMarkerIndex = -1;
      for (let index = 0; index < this.markers.length; index++) {
        const marker = this.markers[index];
        if (marker.place_id == newVal) {
          selectedMarkerIndex = index;
          break;
        }
      }
      if (selectedMarkerIndex != -1) {
        this.toggleInfoWindow(
          this.markers[selectedMarkerIndex],
          selectedMarkerIndex
        );
      } else if (this.popup) {
        this.popup.remove();
      }
    },
  },
  computed: {
    markersState() {
      return (this.markers || []).map((m) => ({
        position: m.position,
        icon: m.icon,
        infoText: m.infoText,
      }));
    },
  },
  mounted() {
    // non-reactive map objects
    this.map = null;
    this.mapMarkers = [];
    this.popup = null;

    loadMapboxGl()
      .then((mapboxgl) => {
        if (this._isDestroyed) return;
        this.mapboxgl = mapboxgl;
        mapboxgl.accessToken = this.config.mapboxToken;
        this.map = new mapboxgl.Map({
          container: this.$refs.map,
          style: "mapbox://styles/mapbox/streets-v12",
          center: isValidPosition(this.center) ? toLngLat(this.center) : [0, 0],
          zoom: this.mapboxZoom(this.zoom),
        });
        this.map.addControl(new mapboxgl.NavigationControl());
        this.map.on("click", this.mark);
        this.map.on("load", () => {
          this.map.addSource(POLYLINES_SOURCE, {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
          });
          this.map.addLayer({
            id: POLYLINES_SOURCE,
            type: "line",
            source: POLYLINES_SOURCE,
            layout: { "line-join": "round", "line-cap": "round" },
            paint: { "line-color": ["get", "color"], "line-width": 5 },
          });
          this.renderPolylines();
        });
        this.renderMarkers();
        this.fitBounds();
      })
      .catch((e) => window.alert(e.message));
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    // Google Maps uses 256px tiles and Mapbox 512px tiles, so the same view is one zoom level lower
    mapboxZoom(zoom) {
      return Math.max((zoom || 1) - 1, 0);
    },
    mark(event) {
      if(!this.enabled)
        return;

      reverseGeocode(event.lngLat.lat, event.lngLat.lng, this.config)
        .then((place) => {
          if (place) {
            this.$emit('map-click', place)
          } else {
            window.alert("No results found");
          }
        })
        .catch((e) => window.alert("Geocoder failed due to: " + e));
    },
    renderMarkers() {
      if (!this.map) return;
      this.mapMarkers.forEach((marker) => marker.remove());
      this.mapMarkers = [];
      (this.markers || []).forEach((m) => {
        if (!isValidPosition(m.position)) return;
        let options = {};
        if (m.icon) {
          const el = document.createElement("img");
          el.src = m.icon;
          options = { element: el, anchor: "bottom" };
        }
        this.mapMarkers.push(
          new this.mapboxgl.Marker(options).setLngLat(toLngLat(m.position)).addTo(this.map)
        );
      });
      // keep an open info window in sync with its marker
      if (this.popup && this.popup.isOpen() && this.markers[this.currentMidx]) {
        const marker = this.markers[this.currentMidx];
        this.popup.setLngLat(toLngLat(marker.position)).setHTML(marker.infoText || "");
      }
    },
    renderPolylines() {
      if (!this.map) return;
      const source = this.map.getSource(POLYLINES_SOURCE);
      if (!source) return;
      source.setData({
        type: "FeatureCollection",
        features: (this.polylines || []).map((p) => ({
          type: "Feature",
          properties: { color: p.strokeColor || "#000000" },
          geometry: {
            type: "LineString",
            coordinates: (p.data || []).map(toLngLat),
          },
        })),
      });
    },
    fitBounds() {
      if (!this.map) return;
      if(this.markers.length == 1)
      return;
      if (this.markers.length == 0) return;

      const b = new this.mapboxgl.LngLatBounds();
      this.markers.forEach((marker) => {
        if (isValidPosition(marker.position)) {
          b.extend(toLngLat(marker.position));
        }
      });
      if (!b.isEmpty()) {
        this.map.fitBounds(b, { padding: 50, maxZoom: 16 });
      }
    },
    toggleInfoWindow: function (marker, idx) {
      if (!this.map) return;
      if (this.popup == null) {
        this.popup = new this.mapboxgl.Popup({ offset: 35, closeOnClick: false });
      }
      const open = () => {
        this.popup
          .setLngLat(toLngLat(marker.position))
          .setHTML(marker.infoText || "")
          .addTo(this.map);
      };

      //check if its the same marker that was selected if yes toggle
      if (this.currentMidx == idx) {
        if (this.popup.isOpen()) {
          this.popup.remove();
        } else {
          open();
        }
      }
      //if different marker open the popup and reset current marker index
      else {
        open();
        this.currentMidx = idx;
      }
    },
  },
};
</script>
